import React from "react";
import { Link } from "react-router-dom";
import { Music4, Mic2, Drum, Guitar, Sparkles, Clock, Volume2 } from "lucide-react";
import Reveal from "../components/Reveal";
import { IMAGES, SETLIST_HIGHLIGHTS } from "../mock/mock";

const TheShow = () => {
  return (
    <div className="overflow-hidden">
      <section className="pt-40 pb-16 px-5 text-center relative">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-[#e0b04c]/8 rounded-full blur-[130px] pointer-events-none" />
        <Reveal className="relative z-10 max-w-3xl mx-auto">
          <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-5">The Experience</p>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[#f5f0e8] leading-tight">
            A Full-Band <span className="text-gold-gradient">Concert Experience</span>
          </h1>
          <p className="text-[#b3a998] text-lg leading-relaxed mt-7 max-w-2xl mx-auto">
            No shortcuts. No backing tracks. Just seasoned musicians delivering
            the authentic soul, R&amp;B, and funk of Frankie Beverly &amp; Maze
            — live and in full color.
          </p>
        </Reveal>
      </section>

      {/* WHAT YOU GET */}
      <section className="px-5 pb-20">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            { icon: Mic2, t: "Soulful Lead Vocals", d: "Powerful, expressive vocals that capture the warmth and heart of Frankie Beverly." },
            { icon: Drum, t: "Live Rhythm Section", d: "A tight, groove-locked rhythm section that keeps the whole room moving all night." },
            { icon: Guitar, t: "Rich Instrumentation", d: "Guitars, keys, bass, congas, and vibrant brass — the full Maze sound, faithfully recreated." },
            { icon: Volume2, t: "Concert Production", d: "Full production readiness with professional sound, punctual soundchecks, and polish." },
            { icon: Sparkles, t: "All-White Aesthetic", d: "Signature all-white attire that honors the iconic look of the legacy we celebrate." },
            { icon: Clock, t: "Reads the Room", d: "Decades of experience building setlists and dynamics that leave audiences wanting more." },
          ].map((f, i) => (
            <Reveal key={i} delay={i * 90}>
              <div className="h-full p-8 rounded-xl bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/30 hover:-translate-y-1 transition-all duration-400 group">
                <div className="w-14 h-14 rounded-lg bg-[#e0b04c]/10 flex items-center justify-center mb-5 group-hover:bg-[#e0b04c]/20 transition-colors">
                  <f.icon className="text-[#e0b04c]" size={26} />
                </div>
                <h3 className="font-serif-display text-2xl text-[#f5f0e8] mb-3">{f.t}</h3>
                <p className="text-[#9c917f] leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SETLIST */}
      <section className="py-20 px-5 bg-[#0a0806]/60 border-y border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="text-[#e0b04c] text-sm tracking-[0.3em] uppercase font-medium mb-4">Signature Grooves</p>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-6">The Setlist</h2>
            <p className="text-[#b3a998] leading-relaxed mb-8">
              From the very first groove to the final encore, every song is a
              chapter in a 50-year story of love, resilience, and joy. Here are a
              few of the anthems you'll hear.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {SETLIST_HIGHLIGHTS.map((song, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#12100c] border border-white/5 hover:border-[#e0b04c]/30 transition-colors">
                  <Music4 size={16} className="text-[#e0b04c] flex-shrink-0" />
                  <span className="text-[#d8cdb9] text-sm">{song}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150} className="relative group">
            <div className="absolute -inset-3 bg-gradient-to-tr from-[#e0b04c]/20 to-transparent rounded-xl blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
            <img src={IMAGES.heroBand} alt="A.T.O.M. performing live" className="relative rounded-xl w-full object-cover shadow-2xl" />
          </Reveal>
        </div>
      </section>

      {/* EVENT TYPES */}
      <section className="py-24 px-5">
        <Reveal className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#f5f0e8] mb-5">Perfect For Any Stage</h2>
          <p className="text-[#b3a998]">A.T.O.M. delivers a concert-quality experience wherever the moment calls for soul.</p>
        </Reveal>
        <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-3">
          {["Music Festivals", "Theaters & Concert Halls", "Casinos & Resorts", "Corporate Galas", "Private Celebrations", "Outdoor Concert Series", "Alumni Events", "Cruises"].map((t) => (
            <span key={t} className="px-5 py-2.5 rounded-full border border-[#e0b04c]/25 text-[#d8cdb9] text-sm hover:bg-[#e0b04c] hover:text-[#0c0a08] hover:border-[#e0b04c] transition-all duration-300 cursor-default">{t}</span>
          ))}
        </div>
        <Reveal className="text-center mt-14">
          <Link to="/booking" className="inline-flex bg-gradient-to-b from-[#e8bc55] to-[#c99339] text-[#0c0a08] font-semibold px-8 py-4 rounded-md hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(224,176,76,0.4)] transition-all duration-300">
            Book The Full Experience
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default TheShow;
